import {
  type PartyRole,
  SortDirection,
  TransactionColumnName,
} from "@digits-graphql/frontend/graphql-bearer"
import objectHelper from "@digits-shared/helpers/objectHelper"
import type { ColumnSort } from "@tanstack/react-table"
import { FrontendPartyRole } from "src/frontend/types/FrontendPartyRole"

export type TransactionSortKeys =
  | "Oldest"
  | "Newest"
  | "Smallest"
  | "Largest"
  | "Party"
  | "PartyAscending"
  | "Category"

interface TransactionSortDisplayProps {
  partyRole: PartyRole
}

export class TransactionSort {
  static readonly Oldest = new TransactionSort(
    TransactionColumnName.OccurredAt,
    SortDirection.Ascending,
    "Oldest",
    { id: "timestamp", desc: false }
  )

  static readonly Newest = new TransactionSort(
    TransactionColumnName.OccurredAt,
    SortDirection.Descending,
    "Newest",
    { id: "timestamp", desc: true }
  )

  static readonly Smallest = new TransactionSort(
    TransactionColumnName.Amount,
    SortDirection.Ascending,
    "Smallest",
    { id: "moneyFlow", desc: false }
  )

  static readonly Largest = new TransactionSort(
    TransactionColumnName.Amount,
    SortDirection.Descending,
    "Largest",
    { id: "moneyFlow", desc: true }
  )

  static readonly Party = new TransactionSort(
    TransactionColumnName.Party,
    SortDirection.Descending,
    "Party",
    { id: "party", desc: true },
    ({ partyRole }: TransactionSortDisplayProps) =>
      `By ${FrontendPartyRole.displayStringForRole(partyRole)}`
  )

  static readonly PartyAscending = new TransactionSort(
    TransactionColumnName.Party,
    SortDirection.Ascending,
    "PartyAscending",
    { id: "party", desc: false },
    ({ partyRole }: TransactionSortDisplayProps) =>
      `By ${FrontendPartyRole.displayStringForRole(partyRole)}`
  )

  static readonly DEFAULT = TransactionSort.Newest

  static fromKey(key: TransactionSortKeys): TransactionSort | undefined {
    if (key === "Category") return undefined
    return TransactionSort[key]
  }

  static fromColumnAndDirection(
    column: TransactionColumnName,
    direction: SortDirection
  ): TransactionSort {
    return (
      (TransactionSort.all().find(
        (sort) => sort.column === column && sort.direction === direction
      ) as TransactionSort) || TransactionSort.DEFAULT
    )
  }

  static fromTableSort(sorting: ColumnSort | undefined): TransactionSort {
    if (!sorting) return TransactionSort.DEFAULT

    return (
      (TransactionSort.all().find((sort) =>
        objectHelper.shallowEqual(sorting, sort.toTableSort[0])
      ) as TransactionSort) || TransactionSort.DEFAULT
    )
  }

  static all(): TransactionSort[] {
    return [...new Set(Object.values(TransactionSort))]
  }

  readonly column: TransactionColumnName
  readonly direction: SortDirection
  readonly key: TransactionSortKeys
  readonly displayName: (props: TransactionSortDisplayProps) => string
  readonly toTableSort: ColumnSort[]

  private constructor(
    column: TransactionColumnName,
    direction: SortDirection,
    key: TransactionSortKeys,
    toTableSort: ColumnSort,
    displayName?: (props: TransactionSortDisplayProps) => string
  ) {
    this.column = column
    this.direction = direction
    this.key = key
    this.toTableSort = [toTableSort]
    this.displayName = displayName || (() => key)
  }
}
